import { FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { HBRDto, HBRForm } from 'types/ndf';
import { downloadFile } from 'utils/downloadFile';
import { t } from 'utils/getTranslations';
import { isDateEarlierThan, isDateLaterThan, isValidDateRange } from 'utils/validators';
import { NDF_FORM_MAX_DATERANGE_DAYS_DIFFERENCE, NDF_HBR_ENDPOINT } from './ndfConstants';

const defaultValues: HBRForm = {
    employees: [],
    employmentType: null,
    transactionStartDate: null,
    transactionEndDate: null,
};

const EMPLOYMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
    {
        label: 'Cooperation Contracts',
        value: 'COOP',
    },
    {
        label: 'Full-time Employment',
        value: 'FTE',
    },
    {
        label: 'Native Speakers',
        value: 'NATIVE',
    },
];

const NDFExportHBRForm: FC = () => {
    const methods = useForm<HBRForm>({ defaultValues, reValidateMode: 'onChange', mode: 'onChange' });
    const { control, setValue, handleSubmit, trigger, getFieldState } = methods;

    const onSubmit = useCallback((formData: HBRForm) => {
        const data = mapToHBRDto(formData);
        const queryParams = new URLSearchParams(Object.entries(data).filter(([, value]) => value !== null)).toString();
        downloadFile(`${NDF_HBR_ENDPOINT}?${queryParams}`);
    }, []);

    const actions = useMemo(() => {
        return [
            <Button
                key="ndf-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="ndf.export" />
            </Button>,
        ];
    }, [handleSubmit, onSubmit]);

    const dateRangeValidator = useCallback(
        (_: unknown, formValues: HBRForm) =>
            isValidDateRange(
                formValues.transactionStartDate ?? '',
                formValues.transactionEndDate ?? '',
                NDF_FORM_MAX_DATERANGE_DAYS_DIFFERENCE
            ),
        []
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('menu.ndf-export')} actions={actions} />
                <SectionLayout>
                    <Section title="menu.payroll-hbr">
                        <div className="max-w-[576px]">
                            <div className="flex flex-col gap-4">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="transactionStartDate"
                                            label="ndf.hbr.transaction-date-from"
                                            onChange={() => {
                                                const { error } = getFieldState('transactionEndDate');
                                                if (error) {
                                                    trigger('transactionEndDate');
                                                }
                                            }}
                                            fullWidth
                                            required
                                            validate={{
                                                isEarlierThan: (_, formValues) => {
                                                    return isDateEarlierThan(
                                                        formValues.transactionStartDate ?? '',
                                                        formValues.transactionEndDate ?? '',
                                                        t('ndf.hbr.transaction-date-to')
                                                    );
                                                },
                                                isValidRange: dateRangeValidator,
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="transactionEndDate"
                                            label="ndf.hbr.transaction-date-to"
                                            onChange={() => {
                                                const { error } = getFieldState('transactionStartDate');
                                                if (error) {
                                                    trigger('transactionStartDate');
                                                }
                                            }}
                                            fullWidth
                                            required
                                            validate={{
                                                isLaterThan: (_, formValues) => {
                                                    return isDateLaterThan(
                                                        formValues.transactionEndDate ?? '',
                                                        formValues.transactionStartDate ?? '',
                                                        t('ndf.hbr.transaction-date-from')
                                                    );
                                                },
                                                isValidRange: dateRangeValidator,
                                            }}
                                        />
                                    </div>
                                </div>
                                <SelectInput
                                    control={control}
                                    name="employmentType"
                                    label="ndf.hbr.employment-type"
                                    placeholder="ndf.hbr.employment-type-placeholder"
                                    options={EMPLOYMENT_TYPE_OPTIONS}
                                    getOptionIdentifier="value"
                                    getOptionLabel="label"
                                    setValue={setValue}
                                />
                                <DynamicMultiSelectInput
                                    control={control}
                                    name="employees"
                                    label="ndf.hbr.employees"
                                    placeholder="ndf.hbr.employees-placeholder"
                                    setValue={setValue}
                                    paginated={{
                                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                                        searchField: 'fullName',
                                    }}
                                    getOptionLabel="name"
                                    getOptionIdentifier="id"
                                />
                            </div>
                        </div>
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

const mapToHBRDto = (form: HBRForm): HBRDto => ({
    employeeIds: form.employees.length > 0 ? form.employees.map((employee) => employee.id) : null,
    employmentType: form.employmentType?.value || null,
    timecardStartDate: form.transactionStartDate || null,
    timecardEndDate: form.transactionEndDate || null,
});

export default NDFExportHBRForm;
